import React, { useState } from 'react'
import {
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Grid,
  CircularProgress
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import { DateTime } from 'luxon'
import { RenderKardexFilter } from './RenderKardexFilter'
import { useApi } from '../../hooks/useApi'
import { ApiConfig } from '../../config/api'
import axios from 'axios'
import { EnumTypeProcess } from '../../interfaces/Process/Process'
import { Status } from '../../hooks/useFetchState'

export interface InputItem {
  id: number
  option: string
  value: string
}

interface Option {
  code: string
  label: string
}

export interface SelectedFiltersValues {
  initialDate: DateTime | null
  finalDate: DateTime | null
  typeMovementId: number | null
  productOriginId: number | null
  warehouseId: number | null
  zoneId: number | null
  product: string | null
}

export const createKardexRequest = (
  body: SelectedFiltersValues,
  typeProcessId: number
) => {
  const url = new URL(ApiConfig.ProcessApi)
  return axios.post(url.toString(), {
    data: JSON.stringify(body),
    typeProcessId
  })
}
interface KardexFiltersProps {
  openSnackBar: (message: string, isSuccess: boolean) => void
}

export const KardexFilters: React.FC<KardexFiltersProps> = ({
  openSnackBar
}) => {
  const initialInput: InputItem = {
    id: 0,
    option: 'Seleccione una opción...',
    value: ''
  }
  const [inputList, setInputList] = useState<InputItem[]>([initialInput])
  const options: Option[] = [
    { code: 'dateRange', label: 'Rango de Fecha' },
    { code: 'typeMovementId', label: 'Tipo Movimiento' },
    { code: 'product', label: 'Producto' },
    { code: 'productOrigin', label: 'Marca' },
    { code: 'warehouseId', label: 'Almacén' }
  ]

  const [finalRequest, setFinalRequest] = useState<SelectedFiltersValues>({
    initialDate: null,
    finalDate: null,
    productOriginId: null,
    typeMovementId: null,
    warehouseId: null,
    zoneId: null,
    product: null
  })

  const addInputField = () => {
    setInputList([
      ...inputList,
      {
        id: Math.max(...inputList.map((el) => el.id)) + 1,
        option: 'Seleccione una opción...',
        value: ''
      }
    ])
  }

  const handleSelectChange = (
    event: React.ChangeEvent<{ value: unknown }>,
    id: number
  ) => {
    const selectedOption = event.target.value as string
    if (selectedOption === 'Seleccione una opción...') {
      setInputList((prevInputList) =>
        prevInputList.map((input) =>
          input.id === id ? { ...input, option: selectedOption } : input
        )
      )
      return
    }

    const updatedInputList = inputList.map((input) =>
      input.id === id ? { ...input, option: selectedOption } : input
    )
    setInputList(updatedInputList)
  }

  const handleTextChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFinalRequest({
      ...finalRequest,
      [event.target.name]: event.target.value
    })
  }

  const handleRemoveInput = (id: number) => {
    const filteredInputList = inputList.filter((input) => input.id !== id)
    setInputList(filteredInputList)
  }

  const availableOptions = (id: number) => {
    const selectedOption = inputList.find((input) => input.id === id)?.option
    return options.filter(
      (option) =>
        option.code === selectedOption ||
        !inputList.some((input) => input.option === option.code)
    )
  }

  const handleUTCSelectedDateTimeChange = (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    const selectedDateUTC = DateTime.fromISO(e.target.value).toUTC()
    setFinalRequest({
      ...finalRequest,
      [e.target.name]: selectedDateUTC
    })
  }

  const handleChangeDDLOption = (e: any) => {
    setFinalRequest({
      ...finalRequest,
      [e.target.name]: e.target.value
    })
  }

  const {
    state: { status },
    callApi: createKardexProcess
  } = useApi()

  const createKardexProcessCall = () => {
    createKardexProcess(
      createKardexRequest(finalRequest, EnumTypeProcess.RequestKardex)
    )
      .then(() => openSnackBar('Proceso creado correctamente', true))
      .catch(() => openSnackBar('Ocurrió un error al crear el proceso', false))
  }

  return (
    <Grid container spacing={3}>
      <Grid container item xs={12} justify='flex-end'>
        <Button
          variant='contained'
          color='primary'
          onClick={addInputField}
          disabled={
            inputList.length === options.length ||
            availableOptions.length === options.length
          }
        >
          Agregar
        </Button>
      </Grid>
      {inputList.map((input) => (
        <Grid container item xs={12} style={{ margin: '10px' }} key={input.id}>
          <Grid item xs={3}>
            <FormControl variant='outlined' fullWidth>
              <InputLabel id={`select-label-${input.id}`}>
                Seleccione una opción...
              </InputLabel>
              <Select
                labelId={`select-label-${input.id}`}
                value={input.option}
                onChange={(event) => handleSelectChange(event, input.id)}
                label='Seleccione una opción...'
                fullWidth
              >
                <MenuItem value='Seleccione una opción...'>
                  Seleccione una opción...
                </MenuItem>
                {availableOptions(input.id).map((option) => (
                  <MenuItem key={option.code} value={option.code}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid container item xs={5} style={{ padding: '0 10px 0 10px' }}>
            <RenderKardexFilter
              inputItem={input}
              handleChangeDDLOption={handleChangeDDLOption}
              handleUTCSelectedDateTimeChange={handleUTCSelectedDateTimeChange}
              selectedFiltersValues={finalRequest}
              handleProductCode={handleTextChange}
            />
          </Grid>
          <Grid item xs={1}>
            <IconButton
              onClick={() => handleRemoveInput(input.id)}
              disabled={input.id === 0}
            >
              <DeleteIcon />
            </IconButton>
          </Grid>
        </Grid>
      ))}
      <Grid container item xs={12} justify='center'>
        <Button
          variant='contained'
          color='primary'
          onClick={createKardexProcessCall}
          disabled={
            inputList.some((el) => el.option === 'Seleccione una opción...') ||
            status === Status.Loading
          }
          endIcon={
            status === Status.Loading && <CircularProgress size={'small'} />
          }
        >
          Solicitar Kardex
        </Button>
      </Grid>
    </Grid>
  )
}

export default KardexFilters
